import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaMedal,
  FaSearch,
  FaTimes,
  FaTrophy,
  FaUserAlt,
} from "react-icons/fa";

import { getAllPalmares } from "../../services/palmaresServices";
import Reveal from "../../components/Reveal/Reveal";

const FALLBACK = "/img/user_avatar.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) {
    return cleanUrl;
  }

  const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${baseUrl}${cleanUrl}`;
};

const isActivePalmares = (item) => {
  if (
    item?.is_active === undefined ||
    item?.is_active === null ||
    item?.is_active === ""
  ) {
    return true;
  }

  return (
    item.is_active === true || item.is_active === 1 || item.is_active === "1"
  );
};

const dateLabel = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const compactDateLabel = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fullName = (item) => {
  const nom = item?.adherent?.nom || "";
  const prenom = item?.adherent?.prenom || "";

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

const cover = (item) => {
  const photo = toAbsolute(item?.adherent?.photo_url);
  return photo || FALLBACK;
};

const rankLabel = (rank = "") => {
  const value = String(rank || "").trim();

  return value || "Résultat";
};

const getRankStyle = (rank = "") => {
  const value = normalize(rank);

  if (
    value.includes("1") ||
    value.includes("or") ||
    value.includes("premier") ||
    value.includes("1er")
  ) {
    return "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300";
  }

  if (
    value.includes("2") ||
    value.includes("argent") ||
    value.includes("deuxieme")
  ) {
    return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-400/30 dark:bg-slate-400/10 dark:text-slate-200";
  }

  if (
    value.includes("3") ||
    value.includes("bronze") ||
    value.includes("troisieme")
  ) {
    return "border-orange-300 bg-orange-100 text-orange-700 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-300";
  }

  return "border-primary/25 bg-primary/10 text-primary";
};

const PalmaresSkeleton = () => (
  <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <div className="mx-auto h-9 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
        <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="mt-12 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="h-80 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
        <div className="h-80 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  </section>
);

const EmptyState = () => (
  <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaTrophy />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucun palmarès disponible
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Les résultats du club apparaîtront ici dès qu’ils seront ajoutés dans
      l’administration.
    </p>
  </div>
);

function PalmaresRow({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-3 rounded-2xl border p-2 text-left transition",
        active
          ? "border-primary/35 bg-primary/10 shadow-sm"
          : "border-slate-200 bg-white/80 hover:border-primary/25 hover:bg-orange-50/60 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10",
      )}
    >
      <img
        src={cover(item)}
        alt={fullName(item)}
        loading="lazy"
        className="h-14 w-14 shrink-0 rounded-2xl object-cover"
        onError={(event) => {
          event.currentTarget.src = FALLBACK;
        }}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-extrabold uppercase text-slate-950 dark:text-white">
          {item?.adherent?.nom || "Adhérent"}
        </p>

        <p className="truncate text-xs font-bold text-slate-500 dark:text-slate-300">
          {item?.adherent?.prenom || ""}
        </p>

        <p className="mt-1 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
          {item?.titre_palmares || "Compétition"}
        </p>
      </div>

      <div
        className={cn(
          "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-extrabold",
          getRankStyle(item?.classement_palmares),
        )}
      >
        {rankLabel(item?.classement_palmares)}
      </div>
    </button>
  );
}

function PalmaresModal({ current, allForCurrent, onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-3 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-700 shadow-lg backdrop-blur transition hover:text-primary dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>

        <div className="border-b border-slate-200 p-5 dark:border-white/10 sm:p-6">
          <div className="flex items-center gap-4 pr-12">
            <img
              src={cover(current)}
              alt={fullName(current)}
              className="h-16 w-16 rounded-2xl object-cover"
              onError={(event) => {
                event.currentTarget.src = FALLBACK;
              }}
            />

            <div className="min-w-0">
              <p className="font-barlow text-2xl font-extrabold uppercase text-slate-950 dark:text-white">
                {fullName(current)}
              </p>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {allForCurrent.length} palmarès enregistré
                {allForCurrent.length > 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-5 sm:p-6">
          <div className="space-y-3">
            {allForCurrent.map((item) => (
              <div
                key={item.id}
                className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-bold leading-snug text-slate-950 dark:text-white">
                      {item?.titre_palmares || "Compétition"}
                    </p>

                    <p className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <FaCalendarAlt className="text-primary" />
                      {dateLabel(item?.date_palmares) || "Date non renseignée"}
                    </p>
                  </div>

                  <div
                    className={cn(
                      "shrink-0 rounded-2xl border px-3 py-2 text-sm font-extrabold",
                      getRankStyle(item?.classement_palmares),
                    )}
                  >
                    {rankLabel(item?.classement_palmares)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LastPalmares() {
  const [palmares, setPalmares] = useState([]);
  const [index, setIndex] = useState(0);
  const [animDir, setAnimDir] = useState("next");
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchPalmares = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getAllPalmares();
        const activeItems = (Array.isArray(data) ? data : []).filter(
          isActivePalmares,
        );

        if (!mounted) return;

        setPalmares(activeItems);
        setIndex(0);
      } catch (error) {
        console.error("Erreur lors de la récupération des palmarès :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les palmarès.");
          setPalmares([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPalmares();

    return () => {
      mounted = false;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...palmares].sort((a, b) => {
      const dateA = new Date(a.date_palmares || 0).getTime();
      const dateB = new Date(b.date_palmares || 0).getTime();

      return dateB - dateA;
    });
  }, [palmares]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = normalize(query);

    if (!normalizedQuery) return sorted;

    return sorted.filter((item) => {
      const nom = item?.adherent?.nom || "";
      const prenom = item?.adherent?.prenom || "";
      const event = item?.titre_palmares || "";
      const classement = item?.classement_palmares || "";

      const a = normalize(`${nom} ${prenom}`);
      const b = normalize(`${prenom} ${nom}`);
      const c = normalize(event);
      const d = normalize(classement);

      return (
        a.includes(normalizedQuery) ||
        b.includes(normalizedQuery) ||
        c.includes(normalizedQuery) ||
        d.includes(normalizedQuery)
      );
    });
  }, [sorted, query]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  const items = filteredItems;
  const total = items.length;
  const current = items[index] || null;

  const uniqueAthletesCount = useMemo(() => {
    const keys = new Set();

    sorted.forEach((item) => {
      const id = item?.adherent?.id;

      if (id) {
        keys.add(`id-${id}`);
        return;
      }

      keys.add(normalize(fullName(item)));
    });

    return keys.size;
  }, [sorted]);

  const allForCurrent = useMemo(() => {
    if (!current) return [];

    const currentId = current?.adherent?.id;

    let list = currentId
      ? sorted.filter((item) => item?.adherent?.id === currentId)
      : [];

    if (!list.length) {
      const currentKey = normalize(fullName(current));

      list = sorted.filter((item) => normalize(fullName(item)) === currentKey);
    }

    return [...list].sort((a, b) => {
      const dateA = new Date(a.date_palmares || 0).getTime();
      const dateB = new Date(b.date_palmares || 0).getTime();

      return dateB - dateA;
    });
  }, [current, sorted]);

  const go = (nextIndex, direction) => {
    if (!total) return;

    setAnimDir(direction);
    setIndex((nextIndex + total) % total);
  };

  const prev = () => go(index - 1, "prev");
  const next = () => go(index + 1, "next");

  if (loading) return <PalmaresSkeleton />;

  return (
    <section
      id="palmares"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <style>{`
        @keyframes pm-in-next {
          from { opacity: 0; transform: translateX(18px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes pm-in-prev {
          from { opacity: 0; transform: translateX(-18px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .pm-anim {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaTrophy />
            Résultats du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Derniers <span className="text-primary">palmarès</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Les dernières performances du club, présentées de façon compacte et
            facilement consultable.
          </p>
        </Reveal>

        <Reveal
          delay={100}
          className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3"
        >
          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaMedal className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {sorted.length}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Palmarès
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaUserAlt className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {uniqueAthletesCount}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Athlète{uniqueAthletesCount > 1 ? "s" : ""}
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaSearch className="mx-auto mb-3 text-2xl text-primary" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {total}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Résultat{total > 1 ? "s" : ""}
            </p>
          </div>
        </Reveal>

        {errorMsg && (
          <div className="mt-12 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </div>
        )}

        {!errorMsg && !current && (
          <div className="mt-12">
            <EmptyState />
          </div>
        )}

        {!errorMsg && current && (
          <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
            <Reveal variant="left">
              <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
                {total > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prev}
                      className="absolute left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                      aria-label="Palmarès précédent"
                    >
                      <FaChevronLeft />
                    </button>

                    <button
                      type="button"
                      onClick={next}
                      className="absolute right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                      aria-label="Palmarès suivant"
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}

                <div
                  key={current.id || index}
                  className={cn(
                    "pm-anim grid md:grid-cols-[minmax(260px,0.95fr)_minmax(0,1.05fr)]",
                    animDir === "next"
                      ? "[animation:pm-in-next_.32s_ease-out]"
                      : "[animation:pm-in-prev_.32s_ease-out]",
                  )}
                >
                  <div className="relative h-72 overflow-hidden md:h-auto md:min-h-[390px] lg:min-h-[420px]">
                    <img
                      src={cover(current)}
                      alt={fullName(current)}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover object-center"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK;
                      }}
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <p className="inline-flex rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur-xl">
                        À la une
                      </p>

                      <h3 className="mt-4 font-barlow text-3xl font-extrabold uppercase leading-tight text-white drop-shadow-lg">
                        {current?.adherent?.nom}
                      </h3>

                      <p className="font-barlow text-2xl font-bold leading-tight text-white/90">
                        {current?.adherent?.prenom}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col p-6 sm:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                          <FaMedal />
                          Performance
                        </div>

                        <h3 className="mt-5 font-barlow text-3xl font-extrabold leading-tight text-slate-950 dark:text-white">
                          {current?.titre_palmares || "Compétition"}
                        </h3>

                        <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                          <FaCalendarAlt className="text-primary" />
                          {dateLabel(current?.date_palmares) ||
                            "Date non renseignée"}
                        </p>
                      </div>

                      <div
                        className={cn(
                          "rounded-[1.5rem] border px-5 py-4 text-center shadow-sm",
                          getRankStyle(current?.classement_palmares),
                        )}
                      >
                        <p className="font-barlow text-3xl font-extrabold leading-none">
                          {rankLabel(current?.classement_palmares)}
                        </p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.16em] opacity-70">
                          Classement
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                        Palmarès de l’adhérent
                      </p>

                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {fullName(current)} possède{" "}
                        <strong className="text-slate-950 dark:text-white">
                          {allForCurrent.length}
                        </strong>{" "}
                        résultat{allForCurrent.length > 1 ? "s" : ""} publié
                        {allForCurrent.length > 1 ? "s" : ""} dans les palmarès
                        du club.
                      </p>

                      {allForCurrent.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setShowAll(true)}
                          className="mt-4 inline-flex items-center justify-center rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
                        >
                          Voir tous ses palmarès
                        </button>
                      )}
                    </div>

                    {total > 1 && (
                      <div className="mt-auto pt-6">
                        <div className="flex items-center justify-between gap-3 md:hidden">
                          <button
                            type="button"
                            onClick={prev}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                            aria-label="Palmarès précédent"
                          >
                            <FaChevronLeft />
                          </button>

                          <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                            {index + 1} / {total}
                          </p>

                          <button
                            type="button"
                            onClick={next}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                            aria-label="Palmarès suivant"
                          >
                            <FaChevronRight />
                          </button>
                        </div>

                        <div className="hidden justify-center gap-2 md:flex">
                          {items.slice(0, 12).map((item, dotIndex) => (
                            <button
                              key={item.id || dotIndex}
                              type="button"
                              onClick={() =>
                                go(dotIndex, dotIndex > index ? "next" : "prev")
                              }
                              className={cn(
                                "h-2.5 rounded-full transition-all",
                                dotIndex === index
                                  ? "w-8 bg-primary"
                                  : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600",
                              )}
                              aria-label={`Aller au palmarès ${dotIndex + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  aria-hidden
                  className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
                />
              </div>
            </Reveal>

            <Reveal variant="right" delay={100}>
              <aside className="rounded-[2rem] border border-slate-200 bg-white/85 p-4 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-col lg:items-stretch">
                  <div>
                    <h3 className="font-barlow text-2xl font-extrabold text-slate-950 dark:text-white">
                      Liste rapide
                    </h3>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Sélectionnez un résultat sans agrandir la page.
                    </p>
                  </div>

                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

                    <input
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Rechercher..."
                      className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-10 text-sm font-semibold text-slate-700 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
                    />

                    {query && (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 transition hover:text-primary"
                        aria-label="Effacer la recherche"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-4 max-h-[430px] overflow-y-auto pr-1">
                  <div className="space-y-2">
                    {items.map((item, itemIndex) => (
                      <PalmaresRow
                        key={item.id || itemIndex}
                        item={item}
                        active={itemIndex === index}
                        onClick={() =>
                          go(itemIndex, itemIndex > index ? "next" : "prev")
                        }
                      />
                    ))}
                  </div>
                </div>
              </aside>
            </Reveal>
          </div>
        )}
      </div>

      {showAll && current && (
        <PalmaresModal
          current={current}
          allForCurrent={allForCurrent}
          onClose={() => setShowAll(false)}
        />
      )}
    </section>
  );
}
